import { keepPreviousData, useQuery } from '@tanstack/react-query';

import { useApi } from 'src/api';

import { ApiError } from '../api-error';
import { mapGithubApp, mapRepository } from '../mappers/git';
import { apiQuery, getApiQueryKey } from '../query';

import { useOrganization } from './session';

const isNoGithubAppError = (error: unknown) => {
  return ApiError.is(error, 400) && error.message === 'No GitHub Installation';
};

export function useGithubAppQuery(refetchInterval?: number) {
  const organization = useOrganization();
  const api = useApi();

  return useQuery({
    queryKey: getApiQueryKey('get /v1/github/installation', {}, api),
    queryFn: async ({ signal }) => {
      return api('get /v1/github/installation', {}, { signal }).catch((error) => {
        if (isNoGithubAppError(error)) {
          return null;
        } else {
          throw error;
        }
      });
    },
    enabled: organization !== undefined,
    refetchInterval,
    select: (result) => (result ? mapGithubApp(result) : null),
  });
}

export function useGithubApp(refetchInterval?: number) {
  return useGithubAppQuery(refetchInterval).data;
}

export function useRepositoriesQuery(search: string) {
  return useQuery({
    ...apiQuery('get /v1/git/repositories', {
      query: {
        limit: '5',
        name: search || undefined,
      },
    }),
    placeholderData: keepPreviousData,
    select: ({ repositories }) => repositories!.map(mapRepository),
  });
}

export function useRepositories(search: string) {
  return useRepositoriesQuery(search).data ?? [];
}

export function useRepositoryBranchesQuery(repositoryId: string | null, search: string) {
  return useQuery({
    ...apiQuery('get /v1/git/branches', {
      query: {
        limit: '5',
        repository_id: repositoryId!,
        name: search || undefined,
      },
    }),
    enabled: repositoryId !== null,
    placeholderData: keepPreviousData,
    select: ({ branches }) => branches!.map((branch) => branch.name!),
  });
}

export function useRepositoryBranches(repositoryId: string | null, search: string) {
  return useRepositoryBranchesQuery(repositoryId, search).data ?? [];
}
